import Vue from 'vue'
import { debounce } from 'util/ts-utils'
import domUtils from 'util/dom-utils'
import { GalleryConfig as IGalleryConfig } from 'services/api-service'
import { getMediaList, GALLERY_FETCH_LIMIT } from 'services/media-service'
import galleryStore from 'store/gallery-widget-store'
import swipeMixin from 'mixins/swipe-mixin'
import GalleryItem from './gallery-item'
import Pagination from 'objects/pagination/pagination'
import ProgressBar from 'objects/progress/progress-bar'



import withRender from './gallery.html'
import './gallery.scss'

export default withRender(Vue.extend({
  data() {
    return {
      currentPage: 1,
      debouncedLoadMedia: undefined,
      isFetching: false,
      forceLoadMore: false,
      galleryStore,
      galleryWidth: 0,
      media: [],
      mediaSize: 0,
      offset: 0,
      overlayBtnX: 0,
      showCaptions: false,
      startingMediaModalIndex: 0,
      total: 0
    }
  },
  components: {
    'tt-gallery-item': GalleryItem,
    'tt-pagination': Pagination,
    'tt-progress-bar': ProgressBar
  },
  props: {
    galleryConfig: Object,
    // this exists in the galleryObject, but depends on reviews summary headingLevel
    // when embedded
    headingLevel: { type: Number, default: 2 },
    skus: Array,
    tags: Array
  },
  mixins: [swipeMixin],
  computed: {
    galleryTransform() {
      const pageDifferential = this.singleImageWidth * this.imagesPerPage
      const translate = (this.currentPage - 1) * pageDifferential
      return `width: ${this.mediaSize}px; transform: translateX(-${translate}px);`
    },
    imagesPerPage() {
      return Math.floor(this.galleryWidth / this.singleImageWidth)
    },
    paginationBottom() {
      return this.galleryConfig.arrowLocation === 'bottom'
    },
    paginationAsOverlay() {
      return this.galleryConfig.arrowLocation === 'overlay'
    },
    paginationTop() {
      return this.galleryConfig.arrowLocation === 'top'
    },
    paginationTotal() {
      // Since we allow the first image of the next page to display as much as possible, it may
      // appear that it is actually on the current page. When it is on the next to the last page,
      // it doesn't make a lot of sense to have to scroll to the next page.
      // The purprose here is to "fool" the pagination into believing that there is one less,
      // so it doesn't have a last page to go to.
      if (this.mediaSize > 0 && this.imagesPerPage > 0 && this.imagesPerPage < this.total) {
        const spaceOver = this.galleryWidth - (this.imagesPerPage * this.singleImageWidth)
        const percentOver = ((this.mediaSize - spaceOver) / this.mediaSize) * 100
        if (percentOver < 15) {
          return this.total - 1
        }
      }
      return this.total
    },
    showGallery() {
      return this.media.length >= this.galleryConfig.minContentCount
    },
    singleImageWidth() {
      return this.mediaSize + this.galleryConfig.columnSpacing
    },
    numberOfPages() {
      return Math.ceil(this.paginationTotal / this.imagesPerPage)
    },
    useButtonArrowStyle() {
      return this.galleryConfig.arrowStyle === 'button'
    },
    visibleStartIndex() {
      return this.imagesPerPage * (this.currentPage - 1)
    },
    visibleEndIndex() {
      return this.visibleStartIndex + this.imagesPerPage
    }
  },
  methods: {
    bindResize() {
      window.removeEventListener('resize', this.onResize, false)
      window.addEventListener('resize', this.onResize, false)
    },
    loadMedia() {
      this.debouncedLoadMedia()
    },
    loadMediaFn() {
      if (this.skus === undefined ||
        ((this.isFetching || !this.shouldLoadMedia()) && this.offset !== 0)) {
        return
      }
      this.isFetching = true
      const offsetBeforeCall = this.offset
      if (this.offset === 0) {
        this.$emit('onLoad')
      }
      getMediaList(
        this.skus,
        this.tags,
        this.offset,
        this.galleryConfig
      ).then((data) => {
        // if the offset was changed during this call, most likely it was due to skus changing
        if (offsetBeforeCall !== this.offset) {
          return
        }
        // in case offset was set back to zero and media not cleared out
        if (this.offset === 0) {
          this.media = []
        }

        this.offset += GALLERY_FETCH_LIMIT
        // To help prevent accidental calls to the service, if we didn't get any items this time,
        // we can assume there are no more, so set to the current number of items, otherwise set
        // to the total the service returned unless it is more than max allowed.
        if (data.media.length === 0) {
          this.total = this.media.length
        } else if (data.total > this.galleryConfig.maxContentToReturn) {
          this.total = this.galleryConfig.maxContentToReturn
        } else {
          this.total = data.total
        }

        data.media.forEach((mediaItem) => {
          // since the "max" images allowed may cut the actual total short, need to make sure
          // that the number of media items does not exceed that total
          if (this.media.length <= this.total) {
            this.media.push(mediaItem)
          }
        })

        if (this.offset === GALLERY_FETCH_LIMIT) { // first page
          // just need to call when first page is loaded. Will also be called when 'skus' changes.
          // fires an event to allow parent widget decide on whether to call onFinish or not.
          // (only meant to be called if standalone widget)
          this.$nextTick(() => this.$emit('onFinish'))
        }
        // reset in case set before checking again
        this.forceLoadMore = false
        if (this.shouldLoadMedia()) {
          // if we still don't have enough, load more (usually if user skipped ahead)
          // need the timeout due to the debounce
          setTimeout(() => this.loadMedia(), 100)
        }
        this.isFetching = false
      }).catch(() => {
        this.isFetching = false
      })
    },
    isItemHidden(index) {
      return index < this.visibleStartIndex || index > this.visibleEndIndex
    },
    loadMore() { // this is for the media-modal to call through an event, not for gallery pagination
      this.forceLoadMore = true
      this.loadMedia()
    },
    openModal(mediaIndex) {
      // tslint:disable-next-line
      import(/* webpackChunkName: 'media-modal' */ 'components/media-modal/media-modal-loader').then((mediaModalLoader) => {
        mediaModalLoader.default.load({
          callingComponent: this,
          media: this.media,
          startingIndex: mediaIndex,
          moreToLoad: () => this.total > this.media.length
        })
      })
    },
    onResize: debounce(function cb() {
      // needed to use function cb() instead of () =>, so we have the proper context of "this".
      // using debounce so recalculate() isn't called until the event firing has stopped long enough
      this.recalculate()
    }, 100),
    pageChange(pageNum) {
      if (pageNum >= 1 && pageNum <= this.numberOfPages) {
        // setting the page first will allow the gallery to move and pagination page to change
        this.currentPage = pageNum
        // TT-11856 - focus on other pagination button if reached beginning or end
        if (this.numberOfPages > 1 && pageNum === 1) {
          domUtils.safeFocus(() => this.$el.querySelector('.tt-o-pagination__next'))
        } else if (pageNum === this.numberOfPages) {
          domUtils.safeFocus(() => this.$el.querySelector('.tt-o-pagination__back'))
        }
        // we call this every time since it checks to see if more need to be loaded
        this.loadMedia()
      }
    },
    recalculate() {
      // if not on the 1st page, we want to "somewhat" maintain where the user was in the
      // pagination.  We will do this by making sure whatever item is currently displayed first,
      // is displayed as part of the page after recalculation.
      // First we need to find which item that is...
      const previousFirstIndex = this.imagesPerPage * (this.currentPage - 1)
      // then we recalculate the media size and width of the gallery
      this.galleryWidth = this.$el.clientWidth
      // get the highest breakpoint where viewport is closest, but equal or above the
      // container width so we can get the media size
      const breakpoint = (this.galleryConfig as IGalleryConfig).breakpoints
        .filter(item => this.galleryWidth >= item.viewport)
        .sort((a, b) => a.viewport > b.viewport ? -1 : 1)[0]
      this.mediaSize = breakpoint.size
      this.showCaptions = breakpoint.showCaptions
      // now we can set the current page to something else if need be
      if (this.currentPage > 1) {
        // set the page to whatever page that previousFirstIndex is in
        this.currentPage = Math.ceil((previousFirstIndex + 1) / this.imagesPerPage)
      }
      if (this.paginationAsOverlay && this.numberOfPages > 1) {
        // when in overlay, this centers the buttons over the image (not including the captions)
        const btnSelectFn = () => this.$el.querySelector('.tt-o-pagination__next')
        domUtils.whenElementIsVisible(btnSelectFn).then(() => {
          const buttonHeight = this.$el.querySelector('.tt-o-pagination__next').clientHeight
          this.overlayBtnX = (this.mediaSize / 2) - (buttonHeight / 2) + 4
        }).catch(() => {})
      }
    },
    shouldLoadMedia() {
      if (this.forceLoadMore) {
        return true
      }
      // caclulate how many media items need to load knowing we want to stay 3 pages ahead
      const numOfItemsToLoad = (this.currentPage + 3) * this.imagesPerPage
      // then determine if we need to load more...
      if (numOfItemsToLoad > this.total) {
        return this.total > this.media.length
      }
      return numOfItemsToLoad > this.media.length
    },
    swipeLeft() {
      // called from touch mixin, scrolling left means going forward
      this.pageChange(this.currentPage + 1)
    },
    swipeRight() {
      // called from touch mixin, scrolling right means going back
      this.pageChange(this.currentPage - 1)
    }
  },
  created() {
    // TT-11257 - setting on "this" instead of former way in which it was defined as a debounce
    // on the class.  Was conflicting when gallery was included in the page twice, standalone
    // and part of reviews summary
    this.debouncedLoadMedia = debounce(this.loadMediaFn, 100)
    if (this.skus !== undefined) {
      this.loadMedia()
    }
    // for media-modal to call to get more media if needed
    this.$on('loadMore', this.loadMore)
  },
  mounted() {
    domUtils.whenElementIsVisible(() => this.$el, 50).then(() => {
      this.recalculate()
      this.bindResize()
      // the register and preventVertical are in the swipe-mixin
      this.preventVertical = true
      this.registerTouchContainer(this.$refs.galleryOuterEl)
      // after mounted, wait a couple seconds, then load more to stay ahead
      if (this.shouldLoadMedia()) {
        setTimeout(() => this.loadMedia(), 3000)
      }
    }).catch(() => {})
  },
  watch: {
    skus() {
      this.offset = 0
      this.currentPage = 1
      this.media = []
      this.loadMedia()
    }
  },
  beforeDestroy() {
    // shouldn't be called as it should always be around, but just in case
    window.removeEventListener('resize', this.onResize, false)
  }
}))
